// Thin fetch wrapper for the auth/profile API. Access tokens are short-lived
// (5 min), so we persist the refresh token and transparently refresh once on a
// 401 before retrying the request.
const API_BASE = process.env.REACT_APP_API_BASE || '';

const ACCESS_KEY = 'tcas_access';
const REFRESH_KEY = 'tcas_refresh';

export const tokens = {
  get access() { try { return localStorage.getItem(ACCESS_KEY); } catch { return null; } },
  get refresh() { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } },
  set({ access, refresh }) {
    try {
      if (access) localStorage.setItem(ACCESS_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch { /* ignore */ }
  },
  clear() {
    try { localStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  },
};

async function parseError(res) {
  let detail;
  try { detail = await res.json(); } catch { detail = null; }
  const err = new Error(`HTTP ${res.status}`);
  err.status = res.status;
  err.data = detail;
  return err;
}

async function refreshAccess() {
  const refresh = tokens.refresh;
  if (!refresh) return false;
  const res = await fetch(`${API_BASE}/api/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) { tokens.clear(); return false; }
  const data = await res.json();
  tokens.set({ access: data.access, refresh: data.refresh });
  return true;
}

// Authenticated JSON request with one transparent refresh+retry on 401.
async function authFetch(path, { method = 'GET', body } = {}) {
  const doFetch = () => fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let res = await doFetch();
  if (res.status === 401 && await refreshAccess()) {
    res = await doFetch();
  }
  if (!res.ok) throw await parseError(res);
  return res.status === 204 ? null : res.json();
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw await parseError(res);
  const data = await res.json();
  tokens.set({ access: data.access, refresh: data.refresh });
  return data;
}

export async function register(payload) {
  const res = await fetch(`${API_BASE}/api/auth/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export const getMe = () => authFetch('/api/auth/me/');
export const updateMe = (payload) => authFetch('/api/auth/me/', { method: 'PATCH', body: payload });
// params: { spd_kmh, miss_m, start_range_m } — all optional, backend clamps and
// returns the resulting plan (duration_s, t_cpa_s, max_level, ...).
export const testThreat = (params = {}) =>
  authFetch('/api/auth/test-threat/', { method: 'POST', body: params });
// Sends one real Pushover push to the given key (or the saved one when omitted) so
// the pilot can confirm delivery. The key is not saved by this call.
export const testPush = (pushover_user_key) =>
  authFetch('/api/auth/test-push/', { method: 'POST', body: { pushover_user_key } });
export const getMyIncidents = (page = 1, pageSize = 10) =>
  authFetch(`/api/incidents/mine/?page=${page}&page_size=${pageSize}`);
export const getOwntracksConfig = () => authFetch('/api/auth/owntracks-config/');
export const detectLocationKeys = (lat, lng, source) =>
  authFetch('/api/auth/detect-location-keys/', {
    method: 'POST',
    body: source ? { lat, lng, source } : { lat, lng },
  });
export const selectLocationKey = (source, id) =>
  authFetch('/api/auth/select-location-key/', { method: 'POST', body: { source, id } });
export const logout = () => tokens.clear();

// ── Radar: "ignore unless CRITICAL" ──────────────────────────────────────────
// Username-scoped like the rest of /api/tcas/{username}/: no JWT, authenticated by
// the radar link token (`?t=`) so these work on a deep-link opened from a push.
// Muting only suppresses the Pushover push for that aircraft; CRITICAL still gets
// through.
async function tcasPost(username, path, token, body) {
  const query = token ? `?t=${encodeURIComponent(token)}` : '';
  const res = await fetch(`${API_BASE}/api/tcas/${encodeURIComponent(username)}/${path}/${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

const acIdent = (ac) => ({ icao24: ac.icao24 || null, callsign: ac.callsign || null });
export const muteAircraft = (username, ac, token, hours) =>
  tcasPost(username, 'mute', token, { ...acIdent(ac), ...(hours ? { hours } : {}) });
export const unmuteAircraft = (username, ac, token) =>
  tcasPost(username, 'unmute', token, acIdent(ac));
